import { Plus, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useCatalogoExamesConsultaControllerBuscarAnalitos } from '@/api/generated/catalogo-exames/catalogo-exames';
import type { AnalitoParaEscolhaResponse } from '@/api/generated/model';
import {
  comAnalito,
  linhaVazia,
  unidadeDiverge,
  type ErrosDosValores,
  type ValorDoLaudo,
} from '@/features/exames/valoresDoLaudo';
import { CLASSE_CONTROLE } from '@/shared/components/Campo';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { ComboBox, type OpcaoCombo } from '@/shared/ui/combobox';
import { Input } from '@/shared/ui/input';

interface Props {
  valores: ValorDoLaudo[];
  aoMudar: (valores: ValorDoLaudo[]) => void;
  /** Mensagem por `chave` de linha — de `validarValores`. */
  erros?: ErrosDosValores;
}

/**
 * A REVISÃO dos valores de um laudo externo — uma linha por valor do papel.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  É aqui que um número lido por uma IA ganha o direito de virar ponto de
 *  gráfico: a pessoa vê o nome que estava no laudo, o analito que o sistema
 *  propôs para ele, o valor e a unidade — e confirma, troca ou tira.
 *
 *  ⚠️ O que esta tela NUNCA esconde:
 *   · linha sem analito diz "Fora do gráfico" — do contrário a pessoa salva e
 *     vai procurar o ponto que nunca existiu;
 *   · unidade do laudo diferente da do catálogo acende um aviso — o valor
 *     iria para o mesmo eixo em outra escala, e nada estouraria.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Controlado (`valores` / `aoMudar`), sem saber de formulário: é usado dentro
 * do react-hook-form no cadastro e com `useState` na edição.
 */
export function RevisaoDeValores({ valores, aoMudar, erros = {} }: Props) {
  function trocar(chave: string, mudanca: (linha: ValorDoLaudo) => ValorDoLaudo) {
    aoMudar(valores.map((linha) => (linha.chave === chave ? mudanca(linha) : linha)));
  }

  // Analito já usado em outra linha aparece desabilitado no seletor: dois
  // valores não cabem no mesmo analito do mesmo exame.
  const usados = useMemo(
    () => new Map(valores.filter((v) => v.analitoId).map((v) => [v.analitoId, v.chave])),
    [valores],
  );

  return (
    <div className="space-y-3">
      {valores.length === 0 ? (
        <p className="rounded-md border border-dashed border-brand/30 px-3 py-4 text-center text-sm text-muted-foreground">
          Nenhum valor ainda. Anexe o laudo para preencher automaticamente, ou adicione à mão.
        </p>
      ) : (
        <ul className="space-y-2">
          {valores.map((linha, indice) => {
            const numero = indice + 1;
            const erro = erros[linha.chave];

            return (
              <li
                key={linha.chave}
                className="rounded-md border border-brand/20 bg-surface-raised p-3 space-y-2"
              >
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_6.5rem_6rem_auto] sm:items-start">
                  <Input
                    value={linha.nomeNoLaudo}
                    onChange={(e) =>
                      trocar(linha.chave, (l) => ({ ...l, nomeNoLaudo: e.target.value }))
                    }
                    placeholder="Nome no laudo"
                    aria-label={`Nome no laudo do valor ${numero}`}
                    className={CLASSE_CONTROLE}
                  />

                  <SeletorDeAnalito
                    linha={linha}
                    numero={numero}
                    usados={usados}
                    aoEscolher={(analito) => trocar(linha.chave, (l) => comAnalito(l, analito))}
                  />

                  <Input
                    value={linha.valor}
                    onChange={(e) => trocar(linha.chave, (l) => ({ ...l, valor: e.target.value }))}
                    placeholder="Valor"
                    inputMode="decimal"
                    aria-label={`Valor ${numero}`}
                    aria-invalid={erro ? true : undefined}
                    className={CLASSE_CONTROLE}
                  />

                  <Input
                    value={linha.unidade}
                    onChange={(e) =>
                      trocar(linha.chave, (l) => ({ ...l, unidade: e.target.value }))
                    }
                    placeholder="Unidade"
                    aria-label={`Unidade do valor ${numero}`}
                    className={CLASSE_CONTROLE}
                  />

                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0 justify-self-end"
                    aria-label={`Remover o valor ${numero}`}
                    onClick={() => aoMudar(valores.filter((l) => l.chave !== linha.chave))}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                  {!linha.analitoId && (
                    <Badge
                      variant="outline"
                      className="border-amber-500/50 text-amber-700 dark:text-amber-400"
                    >
                      Fora do gráfico
                    </Badge>
                  )}
                  {unidadeDiverge(linha) && (
                    <Badge
                      variant="outline"
                      className="border-amber-500/50 text-amber-700 dark:text-amber-400"
                    >
                      Unidade difere do catálogo ({linha.analitoUnidade})
                    </Badge>
                  )}
                  {linha.referencia && (
                    <span className="text-muted-foreground">
                      Referência no laudo: {linha.referencia}
                    </span>
                  )}
                </div>

                {erro && <p className="text-xs text-destructive">{erro}</p>}
              </li>
            );
          })}
        </ul>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => aoMudar([...valores, linhaVazia()])}
      >
        <Plus className="mr-1.5 h-4 w-4" />
        Adicionar valor
      </Button>
    </div>
  );
}

/** "T4 total — Perfil tireoidiano". Sem o item quando ele não é conhecido. */
function rotuloDoAnalito(nome: string, exameNome: string): string {
  return exameNome ? `${nome} — ${exameNome}` : nome;
}

/** Valor do ComboBox para "desligar do analito". Não colide com uuid. */
const SEM_ANALITO = '__sem-analito__';

/**
 * Escolhe o analito do catálogo a que o valor pertence.
 *
 * Um componente por linha, cada um com a própria busca: a consulta só dispara
 * para a linha em que a pessoa está digitando, e não uma por linha do laudo ao
 * abrir a tela.
 *
 * A ordem das opções é a do que a pessoa mais provavelmente quer:
 *  1. o que o servidor achou parecido com o nome do laudo ("Sugeridos");
 *  2. o resultado da busca no catálogo;
 *  3. "Sem analito" — só quando há o que desligar.
 */
function SeletorDeAnalito({
  linha,
  numero,
  usados,
  aoEscolher,
}: {
  linha: ValorDoLaudo;
  numero: number;
  usados: ReadonlyMap<string, string>;
  aoEscolher: (analito: AnalitoParaEscolhaResponse | null) => void;
}) {
  const [termo, setTermo] = useState('');
  const termoAdiado = useDebounce(termo).trim();

  // Duas letras no mínimo: com uma, a busca devolveria o catálogo inteiro
  // cortado nos 20 primeiros — ruído em vez de resposta.
  const buscando = termoAdiado.length >= 2;
  const { data: encontrados, isFetching } = useCatalogoExamesConsultaControllerBuscarAnalitos(
    { busca: termoAdiado },
    { query: { enabled: buscando, staleTime: 60_000 } },
  );

  const { opcoes, porId } = useMemo(() => {
    const porId = new Map<string, AnalitoParaEscolhaResponse>();
    const opcoes: OpcaoCombo[] = [];

    const acrescentar = (analito: AnalitoParaEscolhaResponse, secao: string) => {
      if (porId.has(analito.id)) return;
      porId.set(analito.id, analito);

      const dono = usados.get(analito.id);
      opcoes.push({
        valor: analito.id,
        // O item vai no RÓTULO, e não só no detalhe: é o rótulo que o gatilho
        // mostra depois de escolhido, e "Resultado" sozinho não diz de quê.
        rotulo: rotuloDoAnalito(analito.nome, analito.exameNome),
        detalhe: analito.unidade ?? undefined,
        desabilitada: dono !== undefined && dono !== linha.chave,
        secao,
      });
    };

    for (const candidato of linha.candidatos)
      acrescentar(candidato, 'Sugeridos pelo nome do laudo');
    for (const analito of buscando ? (encontrados ?? []) : []) acrescentar(analito, 'Catálogo');

    if (linha.analitoId) {
      opcoes.push({
        valor: SEM_ANALITO,
        rotulo: 'Sem analito',
        detalhe: 'O valor fica no laudo, fora do gráfico',
      });
    }

    return { opcoes, porId };
  }, [buscando, encontrados, linha.analitoId, linha.candidatos, linha.chave, usados]);

  return (
    <ComboBox
      aria-label={`Analito do valor ${numero}`}
      valor={linha.analitoId}
      rotuloSelecionado={
        linha.analitoId ? rotuloDoAnalito(linha.analitoNome, linha.analitoExame) : ''
      }
      aoEscolher={(valor) => aoEscolher(valor === SEM_ANALITO ? null : (porId.get(valor) ?? null))}
      opcoes={opcoes}
      placeholder="Escolha o analito"
      placeholderBusca="Buscar no catálogo..."
      vazio={buscando ? 'Nenhum analito encontrado.' : 'Digite para buscar no catálogo.'}
      aoBuscar={setTermo}
      carregando={isFetching}
      className={CLASSE_CONTROLE}
    />
  );
}
